import { Button } from "@heroui/react";
import { CalendarDays, FileText, MapPin, Pencil, Plus, Trash2, Users } from "lucide-react";

import MemberAvatar from "@/components/members/member-avatar";
import { formatEventDate, formatTime12h } from "@/utils/dates";
import { buildMapsUrl } from "@/utils/meets";

const CARD =
  "rounded-2xl border border-accent/10 bg-white shadow-[0_2px_16px_0_rgba(88,80,200,0.05),0_1.5px_1px_0_rgba(255,255,255,0.72)_inset,0_-1px_1px_0_rgba(0,0,0,0.03)_inset] dark:border-[#FFFFFF26] dark:bg-[#FFFFFF21] dark:shadow-none";
const CARD_EMPTY =
  "rounded-2xl border border-dashed border-accent/30 text-accent dark:border-[#FFFFFF26] dark:bg-[#FFFFFF21] dark:text-white";
const ICON_BUBBLE =
  "flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent-soft text-accent dark:bg-[#FFFFFF21] dark:text-white";

const EditButton = ({ onClick, label }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    className="ml-auto flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-soft text-accent transition-opacity hover:opacity-80 dark:bg-[#FFFFFF21] dark:text-white"
  >
    <Pencil size={15} />
  </button>
);

const ConfirmStep = ({ formData, updateFormData, onEditParticipants, onEditDate, onEditLocation, onEditTitle }) => {
  const { title, date, startTime, endTime, location, description, participants = [] } = formData;

  const formattedDate = formatEventDate(date);
  const formattedStart = formatTime12h(startTime);
  const formattedEnd = formatTime12h(endTime);
  const mapsUrl = buildMapsUrl(location);

  const removeParticipant = (id) => {
    updateFormData({ participants: participants.filter((p) => String(p.id) !== String(id)) });
  };

  return (
    <div className="flex max-w-4xl flex-col gap-5">
      <div className="flex items-start gap-3">
        <h4 className="text-2xl font-bold tracking-tight">{title || "Untitled meeting"}</h4>
        {onEditTitle && <EditButton onClick={onEditTitle} label="Edit title" />}
      </div>

      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2">
        {/* ── Left column ───────────────────────────────────────────────── */}
        <div className="flex flex-col gap-3">
          <div className={`flex items-center gap-3 px-3 py-3 ${CARD}`}>
            <div className={ICON_BUBBLE}>
              <CalendarDays size={20} strokeWidth={1} />
            </div>
            <div className="flex min-w-0 flex-col">
              <span className="text-sm font-semibold">{formattedDate || "No date set"}</span>
              {(formattedStart || formattedEnd) && (
                <span className="text-xs text-muted">
                  {formattedStart}
                  {formattedStart && formattedEnd && " – "}
                  {formattedEnd}
                </span>
              )}
            </div>
            <EditButton onClick={() => onEditDate?.()} label="Edit date and time" />
          </div>

          {location ? (
            <div className={`flex items-center gap-3 px-3 py-3 ${CARD}`}>
              <div className={ICON_BUBBLE}>
                <MapPin size={20} strokeWidth={1} />
              </div>
              <div className="flex min-w-0 flex-col">
                {mapsUrl ? (
                  <a
                    href={mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="truncate text-sm font-semibold hover:underline"
                  >
                    {location.name}
                  </a>
                ) : (
                  <span className="truncate text-sm font-semibold">{location.name}</span>
                )}
                {location.description && <span className="truncate text-xs text-muted">{location.description}</span>}
              </div>
              <EditButton onClick={() => onEditLocation?.()} label="Edit location" />
            </div>
          ) : (
            <button
              type="button"
              onClick={() => onEditLocation?.()}
              className={`flex items-center gap-3 px-3 py-3 text-left ${CARD_EMPTY}`}
            >
              <div className={ICON_BUBBLE}>
                <MapPin size={20} strokeWidth={1} />
              </div>
              <span className="text-sm font-semibold">Add location</span>
              <Plus size={16} className="ml-auto" />
            </button>
          )}

          {description && (
            <div className={`flex items-start gap-3 px-3 py-3 ${CARD}`}>
              <div className={ICON_BUBBLE}>
                <FileText size={20} strokeWidth={1} />
              </div>
              <span className="whitespace-pre-wrap py-1 text-sm text-foreground">{description}</span>
            </div>
          )}
        </div>

        {/* ── Right column ──────────────────────────────────────────────── */}
        {participants.length > 0 ? (
          <div className={`flex flex-col ${CARD}`}>
            <div className="flex items-center gap-2.5 border-b border-separator px-3 py-3">
              <div className={ICON_BUBBLE}>
                <Users size={20} strokeWidth={1} />
              </div>
              <span className="font-semibold">
                {participants.length} participant{participants.length !== 1 ? "s" : ""}
              </span>
              <EditButton onClick={() => onEditParticipants?.()} label="Edit participants" />
            </div>

            <ul className="flex max-h-[300px] flex-col gap-1 overflow-y-auto px-3 py-2">
              {participants.map((member) => (
                <li key={member.id} className="flex items-center gap-2 py-1">
                  <MemberAvatar member={member} color="accent" />
                  <span className="min-w-0 flex-1 truncate text-sm font-bold capitalize">{member.fullname}</span>
                  <button
                    type="button"
                    onClick={() => removeParticipant(member.id)}
                    aria-label={`Remove ${member.fullname}`}
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-muted transition-colors hover:bg-danger-soft hover:text-danger"
                  >
                    <Trash2 size={15} />
                  </button>
                </li>
              ))}
            </ul>

            <div className="px-3 pb-3">
              <Button
                size="sm"
                variant="outline"
                fullWidth
                onClick={() => onEditParticipants?.()}
                className="border-accent text-accent dark:border-[#FFFFFF26] dark:text-white"
              >
                Add participants
                <Plus size={14} />
              </Button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => onEditParticipants?.()}
            className={`flex items-center gap-3 px-3 py-3 text-left ${CARD_EMPTY}`}
          >
            <div className={ICON_BUBBLE}>
              <Users size={20} strokeWidth={1} />
            </div>
            <span className="text-sm font-semibold">Add participants</span>
            <Plus size={16} className="ml-auto" />
          </button>
        )}
      </div>
    </div>
  );
};

export default ConfirmStep;